// src/pages/AdminPage/tabs/DashboardTab.tsx
import { Users, BookOpen, ChevronRight } from "lucide-react";
import type { AdminTab, DashStats, DayStats } from "../utils/adminTypes";

interface DashboardTabProps {
  stats: DashStats | null;
  growth: DayStats[];
  growthPeriod: "week" | "month" | "year";
  setGrowthPeriod: (p: "week" | "month" | "year") => void;
  loading: boolean;
  setTab: (tab: AdminTab) => void;
}

export default function DashboardTab({
  stats,
  growth,
  growthPeriod,
  setGrowthPeriod,
  loading,
  setTab,
}: DashboardTabProps) {
  const maxGrowth = Math.max(...growth.map((d) => d.count), 1);

  const statCards = [
    {
      label: "Jami foydalanuvchilar",
      value: stats?.totalUsers ?? "—",
      color: "#3B82F6",
      bg: "rgba(59,130,246,.1)",
      icon: "👥",
    },
    {
      label: "Premium",
      value: stats?.premiumUsers ?? "—",
      color: "#F59E0B",
      bg: "rgba(245,158,11,.1)",
      icon: "⭐",
    },
    {
      label: "Bugun kirgan",
      value: stats?.todayUsers ?? "—",
      color: "#22C55E",
      bg: "rgba(34,197,94,.1)",
      icon: "📅",
    },
    {
      label: "Hafta ichida",
      value: stats?.weekUsers ?? "—",
      color: "#8B5CF6",
      bg: "rgba(139,92,246,.1)",
      icon: "📊",
    },
    {
      label: "Jami savollar",
      value: stats?.totalQuestions ?? "—",
      color: "#06B6D4",
      bg: "rgba(6,182,212,.1)",
      icon: "❓",
    },
    {
      label: "Biletlar",
      value: stats?.totalTickets ?? "—",
      color: "#EC4899",
      bg: "rgba(236,72,153,.1)",
      icon: "🎫",
    },
  ];

  const quickLinks: {
    label: string;
    icon: any;
    color: string;
    tab: AdminTab;
  }[] = [
    {
      label: "Foydalanuvchilarni boshqarish",
      icon: Users,
      color: "#3B82F6",
      tab: "users",
    },
    {
      label: "Savollarni boshqarish",
      icon: BookOpen,
      color: "#22C55E",
      tab: "questions",
    },
  ];

  return (
    <div className="fade-up">
      {/* Stat cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 12,
          marginBottom: 24,
        }}>
        {statCards.map((s, i) => (
          <div
            key={i}
            style={{
              background: "#111827",
              border: "1px solid #1E2D45",
              borderRadius: 16,
              padding: "18px 16px",
            }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
              }}>
              <span style={{ fontSize: 22 }}>{s.icon}</span>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: s.bg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                <span style={{ color: s.color, fontWeight: 900, fontSize: 15 }}>
                  {loading ? "..." : s.value}
                </span>
              </div>
            </div>
            <p
              style={{
                color: "#64748B",
                fontSize: 11,
                fontWeight: 600,
                margin: 0,
              }}>
              {s.label}
            </p>
          </div>
        ))}
      </div>

      {/* Growth chart */}
      <div
        style={{
          background: "#111827",
          border: "1px solid #1E2D45",
          borderRadius: 16,
          padding: "20px 20px 16px",
          marginBottom: 24,
        }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 20,
          }}>
          <div>
            <h3
              style={{
                color: "#fff",
                fontWeight: 800,
                fontSize: 15,
                margin: 0,
              }}>
              Foydalanuvchilar o'sishi
            </h3>
            <p style={{ color: "#475569", fontSize: 12, margin: "2px 0 0" }}>
              Yangi ro'yxatdan o'tganlar
            </p>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {(["week", "month", "year"] as const).map((p) => (
              <button
                key={p}
                onClick={() => setGrowthPeriod(p)}
                style={{
                  padding: "5px 12px",
                  borderRadius: 8,
                  border: `1px solid ${growthPeriod === p ? "#3B82F6" : "#1E2D45"}`,
                  background:
                    growthPeriod === p ? "rgba(59,130,246,.12)" : "transparent",
                  color: growthPeriod === p ? "#3B82F6" : "#64748B",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                }}>
                {p === "week" ?
                  "Hafta"
                : p === "month" ?
                  "Oy"
                : "Yil"}
              </button>
            ))}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: 4,
            height: 140,
            paddingBottom: 8,
          }}>
          {growth.length === 0 ?
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#475569",
                fontSize: 13,
              }}>
              Ma'lumot yo'q
            </div>
          : growth.map((d, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                  minWidth: 0,
                }}>
                <span
                  style={{ color: "#64748B", fontSize: 9, fontWeight: 600 }}>
                  {d.count}
                </span>
                <div
                  style={{
                    width: "100%",
                    background: `rgba(59,130,246,${0.3 + (d.count / maxGrowth) * 0.7})`,
                    borderRadius: "4px 4px 0 0",
                    height: `${Math.max(4, (d.count / maxGrowth) * 100)}%`,
                    minHeight: 4,
                    transition: "height .5s ease",
                  }}
                />
                <span
                  style={{
                    color: "#334155",
                    fontSize: 8,
                    textAlign: "center",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                  }}>
                  {d.date?.slice(5)}
                </span>
              </div>
            ))
          }
        </div>
      </div>

      {/* Quick links */}
      <div
        className="quick-links-grid"
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {quickLinks.map((item, i) => {
          const Icon = item.icon;
          return (
            <button
              key={i}
              onClick={() => setTab(item.tab)}
              className="btn-hover"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "16px",
                borderRadius: 14,
                border: "1px solid #1E2D45",
                background: "#111827",
                cursor: "pointer",
                textAlign: "left",
              }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: `${item.color}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                <Icon size={18} style={{ color: item.color }} />
              </div>
              <span
                style={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 13,
                  flex: 1,
                }}>
                {item.label}
              </span>
              <ChevronRight size={16} style={{ color: "#334155" }} />
            </button>
          );
        })}
      </div>

      <style>{`
        @media (max-width: 480px) {
          .quick-links-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
